import * as stylex from '@stylexjs/stylex';
import { createElement, forwardRef } from 'react';
import { theme } from 'trellis-theming/theme.stylex';
import { DATA_COMPONENT_ATTRIBUTE } from '../../../constants/dataAttributes';
const COMPONENT_NAME = 'Stack';
const baseStyles = {
  base: {
    display: "x78zum5",
    flexDirection: "xdt5ytf",
    $$css: true
  }
};
const spaceStyles = {
  none: {
    gap: "xxhr3t",
    rowGap: null,
    columnGap: null,
    $$css: true
  },
  "2xs": {
    gap: "x1vic7bs",
    rowGap: null,
    columnGap: null,
    $$css: true
  },
  xs: {
    gap: "xx3nbei",
    rowGap: null,
    columnGap: null,
    $$css: true
  },
  sm: {
    gap: "x1obj75",
    rowGap: null,
    columnGap: null,
    $$css: true
  },
  md: {
    gap: "xglmkbt",
    rowGap: null,
    columnGap: null,
    $$css: true
  },
  lg: {
    gap: "x19n7znh",
    rowGap: null,
    columnGap: null,
    $$css: true
  },
  xl: {
    gap: "x16a7io4",
    rowGap: null,
    columnGap: null,
    $$css: true
  }
};
const alignStyles = {
  start: {
    alignItems: "x1cy8zhl",
    $$css: true
  },
  center: {
    alignItems: "x6s0dn4",
    $$css: true
  },
  end: {
    alignItems: "xuk3077",
    $$css: true
  }
};

// Positions the group on the block axis, which is the main axis of the column.
// Only visible when the stack is taller than its content (`fillType`).
const verticalAlignStyles = {
  start: {
    justifyContent: "x1nhvcw1",
    $$css: true
  },
  center: {
    justifyContent: "x2kafvx",
    $$css: true
  },
  end: {
    justifyContent: "x13a6bvl",
    $$css: true
  }
};

// Fills a parent that has a set size.
const fillStyles = {
  fill: {
    height: "x5yr21d",
    width: "xh8yej3",
    minHeight: "x2lwn1j",
    minWidth: "xeuugli",
    $$css: true
  },
  fillRemaining: {
    flexBasis: "x1r8uery",
    flexGrow: "x1iyjqo2",
    flexShrink: "xs83m0k",
    minHeight: "x2lwn1j",
    minWidth: "xeuugli",
    $$css: true
  }
};
function StackConfigInner({
  as = 'div',
  children,
  space = 'none',
  align,
  verticalAlign,
  fillType = 'none',
  id,
  ariaLabel,
  ariaLabelledby,
  ariaDescribedby,
  ariaDescription,
  dataTestId,
  xstyle: __noXstyle
}, ref) {
  const styles = [baseStyles.base, spaceStyles[space]];
  // `align` stays guarded: unset means children stretch to full width.
  if (align) styles.push(alignStyles[align]);
  if (verticalAlign) styles.push(verticalAlignStyles[verticalAlign]);
  if (fillType !== 'none') styles.push(fillStyles[fillType]);
  return /*#__PURE__*/createElement(as, Object.assign({
    ref,
    id: id === null || id === void 0 ? void 0 : id.root,
    'aria-label': ariaLabel === null || ariaLabel === void 0 ? void 0 : ariaLabel.root,
    'aria-labelledby': ariaLabelledby === null || ariaLabelledby === void 0 ? void 0 : ariaLabelledby.root,
    'aria-describedby': ariaDescribedby === null || ariaDescribedby === void 0 ? void 0 : ariaDescribedby.root,
    'aria-description': ariaDescription === null || ariaDescription === void 0 ? void 0 : ariaDescription.root,
    'data-test-id': dataTestId === null || dataTestId === void 0 ? void 0 : dataTestId.root
  }, stylex.props(...styles), {
    [DATA_COMPONENT_ATTRIBUTE]: COMPONENT_NAME
  }), children);
}
StackConfigInner.displayName = 'StackConfigInner';

/**
 * Config-layer vertical layout. One job: consistent vertical rhythm between
 * stacked siblings via `space` — spacing is owned by the parent, not the
 * children. It can also fill its parent or the space left over in a flex
 * parent (`fillType`), and center its content (`align` and
 * `verticalAlign`). Exposes intent props (`space`, `align`, `verticalAlign`,
 * `fillType`, `as`) and structured accessibility attributes.
 *
 * If the available props don't satisfy your use case, reach out in
 * #trellis-support — it directly informs how we evolve the layout components.
 */
export const Stack = /*#__PURE__*/forwardRef(StackConfigInner);
Stack.displayName = COMPONENT_NAME;